import { useMemo, useState, type FormEvent } from 'react'
import { ApiError, api, type Issue } from '../../lib/api'
import {
  RAISE_WORDS,
  checkRaise,
  emptyDraft,
  expiryLimits,
  maxTermWords,
  type RaiseDraft,
  type RaiseKind,
} from '../../lib/acceptance'
import { afterEdit, fromServer, unplaced, type Listed } from '../../lib/issues'
import { personLabel, usePeopleNames } from '../../lib/people'
import type { Save } from '../../lib/save'
import { raiseBody } from '../footers'
import { DialogFooter, Picker } from '../kit'
import { RefusalNote } from '../RefusalNote'
import { checkAfterToday, todayIso } from '../../lib/dateRules'

// The small form behind "Raise the written risk acceptance" and "Ask for a
// contract waiver" on the vendor's approval panel (release 3.2). Four things
// are asked: the reason, what is in place instead, who owns it, and when it
// ends. The kind, the vendor and the title are known already. One sentence
// says who approves it and for how long, so nobody raises one expecting a
// different person to sign it or a longer term than the rules allow.

const LABELS: Record<RaiseKind, { reason: string; control: string }> = {
  RISK_ACCEPTANCE: {
    reason: 'Why this rating is being accepted',
    control: 'Compensating controls',
  },
  CONTRACT_WAIVER: {
    reason: 'Why the contract cannot be filed yet',
    control: 'Compensating controls until it is filed',
  },
}

// The fields this form draws. A problem the server names against anything
// else has no field to sit under.
const DRAWN = ['justification', 'compensatingControl', 'ownerUserId', 'expiresOn']

// Those problems, said above the fields: the footer counts them, and a count
// with nothing to find is worse than none.
export function StrayProblems({ issues, drawn }: { issues: Issue[]; drawn: string[] }) {
  const stray = unplaced(issues, drawn)
  if (stray.length === 0) return null
  return (
    <div className="form-errors" role="alert">
      <ul>
        {stray.map((i) => (
          <li key={i.field + i.code}>{i.detail}</li>
        ))}
      </ul>
    </div>
  )
}

export function RaiseRequestForm({
  kind,
  vendorId,
  vendorName,
  band,
  subject,
  save,
  onRaised,
  onCancel,
}: {
  kind: RaiseKind
  vendorId: string
  vendorName: string
  band: string
  // Who is signed in: the owner it starts with.
  subject: string
  // The save of the dialog this is drawn in (lib/save.ts), keyed by the vendor
  // and the kind. This form goes when the dialog closes and the request does
  // not, so the answer is dealt with there: one request's answer never shuts
  // or empties the other request's form.
  save: Save
  onRaised: (note: string) => void
  onCancel: () => void
}) {
  const names = usePeopleNames()
  const [draft, setDraft] = useState<RaiseDraft>(() => emptyDraft(kind, subject))
  const [issues, setIssues] = useState<Listed[]>([])
  const busy = save.busy
  const [refused, setRefused] = useState<unknown>(null)
  const limits = expiryLimits(kind)
  const words = RAISE_WORDS[kind]
  const labels = LABELS[kind]
  const id = (f: string) => 'raise-' + kind.toLowerCase() + '-' + f

  // The signed-in person first, then everybody the directory knows. A subject
  // the directory does not know is still offered, as itself, because it is who
  // the API will record.
  const owners = useMemo(() => {
    const others = Object.keys(names)
      .filter((s) => s !== subject)
      .sort((a, b) => personLabel(names, a).localeCompare(personLabel(names, b), 'en'))
    const out = subject ? [subject, ...others] : others
    if (draft.ownerUserId && !out.includes(draft.ownerUserId)) out.unshift(draft.ownerUserId)
    return out
  }, [names, subject, draft.ownerUserId])

  // An edit, and the problems that are still problems after it.
  function set<K extends keyof RaiseDraft>(key: K, value: RaiseDraft[K]) {
    const next = { ...draft, [key]: value }
    setDraft(next)
    setIssues((list) => afterEdit(list, key, checkRaise(kind, next)))
  }

  function issueFor(field: string): Issue | undefined {
    return issues.find((i) => i.field === field)
  }

  // D20 of the date rules: said when the person leaves the expiry, not only
  // on Raise. The term limits stay with checkRaise.
  function leaveExpiry() {
    const msg = checkAfterToday('Expiry', draft.expiresOn, todayIso())
    setIssues((cur) => [
      ...cur.filter((i) => i.field !== 'expiresOn'),
      ...(msg ? [{ field: 'expiresOn', code: 'IN_THE_PAST', detail: msg }] : []),
    ])
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setRefused(null)
    const found = checkRaise(kind, draft)
    setIssues(found)
    if (found.length > 0) return
    save.send(api.createException(raiseBody(kind, { id: vendorId, name: vendorName, band }, draft)), {
      done: (reply) => onRaised(reply.note || 'Raised. ' + words.who),
      failed: (err) => {
        const ae = err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
        if (ae.status === 422 && ae.issues.length > 0) setIssues(fromServer(ae.issues))
        else setRefused(ae)
      },
    })
  }

  return (
    <form id={id('form')} className="sw-stack" onSubmit={submit} noValidate data-testid={'raise-' + kind.toLowerCase()}>
      <p className="muted" style={{ margin: 0 }}>
        {words.who}
      </p>

      {refused != null && <RefusalNote error={refused} heading="Nothing was raised" what="The request" verb="raised" />}

      <StrayProblems issues={issues} drawn={DRAWN} />

      <div className="field">
        <label htmlFor={id('reason')}>{labels.reason}</label>
        <textarea
          id={id('reason')}
          rows={3}
          value={draft.justification}
          aria-invalid={issueFor('justification') ? true : undefined}
          aria-describedby={issueFor('justification') ? id('reason-issue') : undefined}
          onChange={(e) => set('justification', e.target.value)}
        />
        {issueFor('justification') && (
          <p className="field-issue" id={id('reason-issue')}>
            {issueFor('justification')?.detail}
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor={id('control')}>{labels.control}</label>
        <textarea
          id={id('control')}
          rows={3}
          value={draft.compensatingControl}
          aria-invalid={issueFor('compensatingControl') ? true : undefined}
          aria-describedby={issueFor('compensatingControl') ? id('control-issue') : undefined}
          onChange={(e) => set('compensatingControl', e.target.value)}
        />
        {issueFor('compensatingControl') && (
          <p className="field-issue" id={id('control-issue')}>
            {issueFor('compensatingControl')?.detail}
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor={id('owner')}>Owner</label>
        {/* Everybody the directory knows. Once somebody is chosen there is no
            way back to nobody, as there was none in the list this replaced. */}
        <Picker
          bare
          id={id('owner')}
          label="Owner"
          value={draft.ownerUserId}
          clearable={false}
          invalid={Boolean(issueFor('ownerUserId'))}
          describedBy={issueFor('ownerUserId') ? id('owner-issue') : undefined}
          options={owners.map((s) => ({
            value: s,
            label: s === subject ? 'You (' + (personLabel(names, s) || 'signed in') + ')' : personLabel(names, s),
            also: [s],
          }))}
          placeholder="Choose the owner: search by name"
          noun="person"
          onChange={(owner) => set('ownerUserId', owner)}
        />
        {issueFor('ownerUserId') && (
          <p className="field-issue" id={id('owner-issue')}>
            {issueFor('ownerUserId')?.detail}
          </p>
        )}
      </div>

      <div className="field">
        <label htmlFor={id('expires')}>Expires on</label>
        <input
          id={id('expires')}
          type="date"
          value={draft.expiresOn}
          min={limits.min}
          max={limits.max}
          aria-invalid={issueFor('expiresOn') ? true : undefined}
          aria-describedby={issueFor('expiresOn') ? id('expires-issue') : id('expires-hint')}
          onChange={(e) => set('expiresOn', e.target.value)}
          onBlur={leaveExpiry}
        />
        <span className="field-note" id={id('expires-hint')}>
          Starts at the longest term, {maxTermWords(kind)}.
        </span>
        {issueFor('expiresOn') && (
          <p className="field-issue" id={id('expires-issue')} role="alert">
            {issueFor('expiresOn')?.detail}
          </p>
        )}
      </div>

      <DialogFooter
        busy={busy}
        problems={issues.length}
        primary={{ label: busy ? 'Raising' : words.submit, form: id('form') }}
        cancel={{ onClick: onCancel }}
      />
    </form>
  )
}
